/**
 * Backend Configuration System
 * Handles environment-based backend switching for Laravel/Node.js
 */

export type BackendType = 'laravel' | 'node'

export interface BackendConfig {
  type: BackendType
  apiUrl: string
  name: string
  description: string
  tokenHeader: string
  tokenPrefix: string
}

// Key to store the last used backend type
const LAST_BACKEND_TYPE_KEY = 'last-backend-type'

/**
 * Clear localStorage and session storage
 */
const clearAuthStorage = () => {
  try {
    // Clear auth-related localStorage keys
    localStorage.removeItem('kt-auth-react-v')
    
    // Clear all localStorage and sessionStorage for clean slate
    localStorage.clear()
    sessionStorage.clear()
    
    console.log('🔄 Backend switched - Authentication storage cleared')
  } catch (error) {
    console.error('Error clearing auth storage:', error)
  }
}

/**
 * Check if backend has changed and clear storage if needed
 */
const checkAndHandleBackendSwitch = (currentBackendType: BackendType) => {
  try {
    const lastBackendType = localStorage.getItem(LAST_BACKEND_TYPE_KEY) as BackendType | null
    
    // Prevent checking during backend switch to avoid loops
    if (localStorage.getItem('backend-switch-in-progress') === 'true') {
      return false
    }
    
    // Only clear storage if there's an actual backend type change
    if (lastBackendType && lastBackendType !== currentBackendType) {
      console.log(`🔄 Backend switch detected: ${lastBackendType} → ${currentBackendType}`)
      // Add a flag to prevent immediate re-initialization loops
      localStorage.setItem('backend-switch-in-progress', 'true')
      
      // Clear storage in a timeout to prevent refresh loops
      setTimeout(() => {
        clearAuthStorage()
        localStorage.removeItem('backend-switch-in-progress')
      }, 100)
      
      return true
    }
    
    // Store current backend type for future comparisons
    localStorage.setItem(LAST_BACKEND_TYPE_KEY, currentBackendType)
    return false
  } catch (error) {
    console.error('Error handling backend switch:', error)
    return false
  }
}

/**
 * Get the active backend configuration from environment variables
 */
export const getActiveBackend = (): BackendConfig => {
  const backendType: BackendType = 'laravel'
  
  // Check for backend switch and clear storage if needed
  checkAndHandleBackendSwitch(backendType)
  
  let apiUrl: string
  let name: string
  let description: string
  let tokenHeader: string
  let tokenPrefix: string
  
  // Laravel backend configuration (default)
  apiUrl = process.env.REACT_APP_API_URL || 'https://rpmd.onyxtec.io/api'
  name = 'Laravel Backend'
  description = 'Laravel API Server'
  tokenHeader = 'Authorization'
  tokenPrefix = 'Bearer '
  
  const config: BackendConfig = {
    type: backendType,
    apiUrl,
    name,
    description,
    tokenHeader,
    tokenPrefix
  }
  
  return config
}

/**
 * Get the current API base URL
 */
export const getCurrentApiUrl = (): string => {
  return getActiveBackend().apiUrl
}

/**
 * Get the current backend type
 */
export const getCurrentBackendType = (): BackendType => {
  return getActiveBackend().type
}

/**
 * Check if a specific backend is currently active
 */
export const isBackendActive = (backendType: BackendType): boolean => {
  return getCurrentBackendType() === backendType
}

/**
 * Validate that the backend configuration is properly set up
 */
export const validateBackendConfig = (): { isValid: boolean; errors: string[] } => {
  const config = getActiveBackend()
  const errors: string[] = []
  
  if (!config.apiUrl) {
    errors.push(`API URL not configured for ${config.type} backend`)
  }
  
  if (!config.apiUrl.startsWith('http')) {
    errors.push(`Invalid API URL format: ${config.apiUrl}`)
  }
  
  if (config.type === 'laravel' && !process.env.REACT_APP_API_URL) {
    errors.push('REACT_APP_API_URL environment variable is required for Laravel backend')
  }
  
  return {
    isValid: errors.length === 0,
    errors
  }
}
